import { FlexRow } from '@/components/common/Flex';
import { useIdvPosition } from '@/components/idv/useIdvPosition';
import {
    Box,
    Tooltip as ChakraTooltip,
    TooltipProps as ChakraTooltipProps,
} from '@chakra-ui/react';
import { IdvPosition } from '@common/react/idv/useBaseIdvPosition';
import { useEffect, useRef, useState } from 'react';
import {
    BarChartSquare02,
    BriefCase01,
    Edit05,
    Fingerprint04,
    UsersUp,
} from '../common/icons';
import { useIdvContext } from './IdvProvider';

type CheckpointPosition = 'invite-partner' | IdvPosition;

export const IdvProgressBar = ({
    includeInviteStep,
    forceStep,
}: {
    includeInviteStep?: boolean;
    forceStep?: CheckpointPosition;
}) => {
    const { steps, position } = useIdvContext();
    const index = getIndex(position, steps);
    const checkpointSteps: CheckpointPosition[] = [
        ...(includeInviteStep ? ['invite-partner' as const] : []),
        'profile',
        'employment-status',
        'investment-priority',
        'consent',
    ];
    const checkpoints = checkpointSteps.map((p) => getIndex(p, steps));
    let activeCheckpoint = checkpoints.reduce(
        (acc, cur) => (index >= cur ? cur : acc),
        0
    );

    if (forceStep) activeCheckpoint = getIndex(forceStep, steps);

    const breadcrumbs = [
        ...(includeInviteStep
            ? [{ label: 'Partner invitation', icon: <UsersUp /> }]
            : []),
        { label: 'About you', icon: <Fingerprint04 /> },
        { label: 'Your work', icon: <BriefCase01 /> },
        { label: 'Investor profile', icon: <BarChartSquare02 /> },
        { label: 'Consent', icon: <Edit05 /> },
    ];

    return (
        <FlexRow alignItems="center">
            {breadcrumbs.map(({ label, icon }, i) => (
                <Tooltip
                    key={label}
                    label={label}
                    currentIndex={index}
                    activeCheckpoint={activeCheckpoint}
                    checkpoint={checkpoints[i]}
                >
                    {icon}
                </Tooltip>
            ))}
        </FlexRow>
    );
};

const getIndex = (position: CheckpointPosition, steps: CheckpointPosition[]) =>
    steps.findIndex((key) => key === position);

interface TooltipProps extends ChakraTooltipProps {
    label: string;
    currentIndex: number;
    activeCheckpoint: number;
    checkpoint: number;
}

const Tooltip = ({
    label,
    children,
    currentIndex,
    activeCheckpoint,
    checkpoint,
    ...props
}: TooltipProps) => {
    const { setPosition, steps } = useIdvContext();
    const idvPosition = useIdvPosition();
    const [isOpen, setIsOpen] = useState(false);
    const timer = useRef<NodeJS.Timeout | null>(null);
    const isActive = activeCheckpoint === checkpoint;
    const isDisabled =
        !isActive &&
        (activeCheckpoint < 0 ||
            checkpoint < 0 ||
            (activeCheckpoint < checkpoint &&
                checkpoint > getIndex(idvPosition, steps)));

    const clearTimer = () => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    };

    const onClick = () => {
        if (isDisabled) return;
        setPosition(steps[checkpoint]);
    };

    useEffect(() => {
        let initTimer: NodeJS.Timeout | null = null;
        if (activeCheckpoint === currentIndex) {
            initTimer = setTimeout(() => {
                setIsOpen(true);
                timer.current = setTimeout(() => {
                    setIsOpen(false);
                    clearTimer();
                }, 2000);
            }, 1000);
        }
        return () => {
            if (initTimer) clearTimeout(initTimer);
            clearTimer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ChakraTooltip
            label={label}
            aria-label={label}
            placement="top"
            isOpen={isActive && timer.current ? isOpen : undefined}
            {...props}
        >
            <Box
                mx={3}
                opacity={isActive ? 1 : 0.2}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                onClick={onClick}
            >
                {children}
            </Box>
        </ChakraTooltip>
    );
};
